import { useEffect, useRef, useState } from "react";
import useCapabilities, { useCanManageTaskNotificationPreferencesBool, useHasRoles } from "@/hooks/useCapabilities";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { ROLE } from "@/providers/AuthorizationProvider/types";

function sameRoles(current: string[] | undefined, next: string[]): boolean {
  return Boolean(current && current.length === next.length && current.every((role, index) => role === next[index]));
}

const DEPARTMENT_NOTIFICATION_ADMIN_ROLES = new Set<string>([
  ROLE.CAREER_SERVICES_ADMIN,
  ROLE.MEMBERSHIP_SERVICES_ADMIN,
  ROLE.EDUCATION_ADMIN,
  ROLE.FINANCE_ADMIN
]);

export type ActorTaskNotificationAccess = {
  loaded: boolean;
  isGlobalAdmin: boolean;
  canManage: boolean;
  actorDepartment: string;
};

function accessFromTokenRoles(roles: string[], actorDepartment: string): ActorTaskNotificationAccess {
  return {
    loaded: true,
    isGlobalAdmin: roles.includes(ROLE.GLOBAL_ADMIN),
    canManage: roles.some((role) => DEPARTMENT_NOTIFICATION_ADMIN_ROLES.has(role)),
    actorDepartment
  };
}

export function useActorTaskNotificationAccess(): ActorTaskNotificationAccess {
  const { loaded } = useCapabilities();
  const meIsGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const meCanManage = useCanManageTaskNotificationPreferencesBool();
  const { assumedUser } = useAssumeUser();
  const { user } = useCurrentUser();
  const actorDepartment = user?.department ?? "";
  const actorSub = user?.sub;
  const accessTokenClaimsRef = useRef(user?.accessTokenClaims);
  accessTokenClaimsRef.current = user?.accessTokenClaims;
  const [tokenRoles, setTokenRoles] = useState<string[] | undefined>(undefined);

  const assumingUser = Boolean(assumedUser);

  useEffect(() => {
    if (!assumingUser) {
      setTokenRoles((current) => (current === undefined ? current : undefined));
      return;
    }

    const accessTokenClaims = accessTokenClaimsRef.current;
    if (!accessTokenClaims) {
      setTokenRoles((current) => (current?.length === 0 ? current : []));
      return;
    }

    let cancelled = false;
    accessTokenClaims()
      .then((claims) => {
        if (cancelled) {
          return;
        }
        const next = claims.backoffice_roles ?? [];
        setTokenRoles((current) => (sameRoles(current, next) ? current : next));
      })
      .catch(() => {
        if (!cancelled) {
          setTokenRoles((current) => (current?.length === 0 ? current : []));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assumingUser, actorSub]);

  if (!assumingUser) {
    return {
      loaded,
      isGlobalAdmin: meIsGlobalAdmin,
      canManage: meCanManage,
      actorDepartment
    };
  }

  if (tokenRoles === undefined) {
    return {
      loaded: false,
      isGlobalAdmin: false,
      canManage: false,
      actorDepartment
    };
  }

  return accessFromTokenRoles(tokenRoles, actorDepartment);
}
